import React from 'react'
import { Checkbox, Divider, Flex, Form, Radio, Select, Switch, Typography } from 'antd'
import { BarChart3, PieChart, Table, Type, Info, LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'
import { SCALE_OPTIONS, MARGIN_OPTIONS, FORMAT_OPTIONS, isImageFormat } from './paperConfig'
import { usePrintSettings } from './PrintSettingsContext'
import { PrintStyleSettings } from '../../../../../redux/context/customReports/types'

const SECTION_TYPE_ICONS: Record<string, LucideIcon> = {
  graph: BarChart3,
  pie: PieChart,
  table: Table,
  text: Type,
  infobox: Info
}

const Panel = styled.div`
  width: 240px;
  min-width: 240px;
  padding: 16px;
  background: #fff;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  .ant-checkbox-wrapper {
    display: flex !important;
    width: 100%;
    > span:last-child {
      min-width: 0;
      overflow: hidden;
      flex: 1;
    }
  }
`

const SectionLabel = styled(Typography.Text)`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #636363;
  display: block;
  margin-bottom: 8px;
`

const FieldGroup = styled.div`
  margin-bottom: 16px;
`

const MARGIN_LABEL_KEYS: Record<string, string> = {
  small: 'marginSmall',
  medium: 'marginMedium',
  large: 'marginLarge'
}

const FORMAT_LABEL_KEYS: Record<string, { label: string; desc: string }> = {
  pdf: { label: 'pdfLabel', desc: 'pdfDesc' },
  png: { label: 'pngLabel', desc: 'pngDesc' },
  jpg: { label: 'jpgLabel', desc: 'jpgDesc' }
}

const PrintSettingsPanel: React.FC = () => {
  const { settings, updateSettings, sections, form } = usePrintSettings()
  const { t } = useTranslation()

  const isImage = isImageFormat(settings.outputFormat)

  return (
    <Panel data-print-ignore>
      <Typography.Title level={5} style={{ margin: '0 0 0 0' }}>
        {t('customReportPrint:exportSettings')}
      </Typography.Title>
      <Divider style={{ margin: '12px 0 16px 0' }} />
      <Form<PrintStyleSettings>
        form={form}
        initialValues={settings}
        onValuesChange={(_, allValues) => updateSettings(allValues)}
      >
        <FieldGroup>
          <SectionLabel>{t('customReportPrint:paperSize')}</SectionLabel>
          <Form.Item name="paperSize" noStyle>
            <Radio.Group style={{ display: 'flex', width: '100%' }}>
              <Radio.Button value="A4" style={{ flex: 1, textAlign: 'center' }}>
                A4
              </Radio.Button>
              <Radio.Button value="A3" style={{ flex: 1, textAlign: 'center' }}>
                A3
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>{t('customReportPrint:orientation')}</SectionLabel>
          <Form.Item name="orientation" noStyle>
            <Radio.Group style={{ display: 'flex', width: '100%' }}>
              <Radio.Button value="portrait" style={{ flex: 1, textAlign: 'center' }}>
                {t('customReportPrint:portrait')}
              </Radio.Button>
              <Radio.Button value="landscape" style={{ flex: 1, textAlign: 'center' }}>
                {t('customReportPrint:landscape')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>{t('customReportPrint:scale')}</SectionLabel>
          <Form.Item name="scale" noStyle>
            <Select options={SCALE_OPTIONS} style={{ width: '100%' }} />
          </Form.Item>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>{t('customReportPrint:margin')}</SectionLabel>
          <Form.Item name="margin" noStyle>
            <Radio.Group style={{ display: 'flex', width: '100%' }}>
              {MARGIN_OPTIONS.map(o => (
                <Radio.Button key={o.value} value={o.value} style={{ flex: 1, textAlign: 'center' }}>
                  {t(`customReportPrint:${MARGIN_LABEL_KEYS[o.value]}`)}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>{t('customReportPrint:fileFormat')}</SectionLabel>
          <Form.Item name="outputFormat" noStyle>
            <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {FORMAT_OPTIONS.map(o => (
                <Radio
                  key={o.value}
                  value={o.value}
                  style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 8, margin: 0 }}
                >
                  <div style={{ marginLeft: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
                      {t(`customReportPrint:${FORMAT_LABEL_KEYS[o.value].label}`)}
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.3 }}>
                      {t(`customReportPrint:${FORMAT_LABEL_KEYS[o.value].desc}`)}
                    </div>
                  </div>
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </FieldGroup>

        <FieldGroup>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SectionLabel style={{ marginBottom: 0 }}>{t('customReportPrint:watermark')}</SectionLabel>
            <Form.Item name="watermark" valuePropName="checked" noStyle>
              <Switch size="small" />
            </Form.Item>
          </div>
        </FieldGroup>

        {!isImage && (
          <>
            <Divider style={{ margin: '8px 0' }} />

            <FieldGroup>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SectionLabel style={{ marginBottom: 0 }}>{t('customReportPrint:pageNumbers')}</SectionLabel>
                <Form.Item name="pageNumbers" valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
              </div>
            </FieldGroup>

            <Divider style={{ margin: '8px 0' }} />

            <FieldGroup>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SectionLabel style={{ marginBottom: 0 }}>{t('customReportPrint:pageBreaks')}</SectionLabel>
                <Form.Item name="pageBreaks" valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
              </div>

              {settings.pageBreaks && sections.length > 0 && (
                <Checkbox.Group
                  value={settings.forcedBreaks}
                  onChange={values => updateSettings({ forcedBreaks: values as number[] })}
                  style={{ marginTop: 8, overflowY: 'auto', width: '100%' }}
                >
                  <Flex vertical gap={2} style={{ minWidth: 0, overflow: 'hidden' }}>
                    {sections.map(s => {
                      const Icon = SECTION_TYPE_ICONS[s.type]
                      return (
                        <Checkbox
                          key={s.id}
                          value={s.id}
                          style={{ fontSize: 13, width: '100%', overflow: 'hidden', alignItems: 'center' }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            {Icon && <Icon size={14} style={{ flexShrink: 0, opacity: 0.5, marginRight: 4 }} />}
                            <span
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={s.name}
                            >
                              {s.name}
                            </span>
                          </span>
                        </Checkbox>
                      )
                    })}
                  </Flex>
                </Checkbox.Group>
              )}
            </FieldGroup>
          </>
        )}
      </Form>
    </Panel>
  )
}

export default PrintSettingsPanel
